import {Container, Sprite, Rectangle, SCALE_MODES, Texture, Point} from 'pixi.js';
import {coreStore} from '@gongxigames/framework/core/globals';

export type CreateCacheSpriteOptions = {
    name: string;
    region: Rectangle;
    provider: ContentProvider;
    resolution?: number;
    immediate?: boolean;
    scaleMode?: SCALE_MODES;
};

export type ContentProvider = Container | (() => Container);

export type TextureAndData = {
    texture: Texture;
    offset: Point;
};

export type CacheData = CreateCacheSpriteOptions & {
    texture?: Texture;
};

export class CacheAsSprite {
    /**
     * if '0' wil use the current renderer resolution
     */
    resolution = 0;

    private cacheData: Map<string, CacheData> = new Map();

    add(options: CreateCacheSpriteOptions): void {
        const data = options as CacheData;

        if (this.cacheData.has(data.name)) {
            return;
        }

        this.cacheData.set(options.name, data);

        if (options.immediate && !data.texture) {
            this.generate(data);
        }
    }

    get(name: string): Sprite {
        const data = this.cacheData.get(name);

        if (!data) {
            throw new Error(`CacheAsSprite: There no cache for: "${name}"`);
        }

        if (!data.texture) {
            this.generate(data);
        }

        const result = new Sprite(data.texture);

        result.position.copyFrom(data.region);
        result.name = name;

        return result;
    }

    getTexture(name: string): TextureAndData {
        const data = this.cacheData.get(name);

        if (!data) {
            throw new Error(`CacheAsSprite: There no cache for: "${name}"`);
        }

        if (!data.texture) {
            this.generate(data);
        }

        return {
            texture: data.texture!,
            offset: new Point(data.region.x, data.region.y)
        };
    }

    pregenerate(names?: string[]): void {
        const {cacheData} = this;

        for (const name of names || cacheData.keys()) {
            const data = cacheData.get(name);

            if (!data) {
                throw new Error(`CacheAsSprite: There no data for: "${name}"`);
            }

            if (!data.texture) {
                this.generate(data);
            }
        }
    }

    remove(name: string): void {
        const data = this.cacheData.get(name);

        if (data) {
            this.cacheData.delete(name);
            this.destroy(data);
        }
    }

    removeTexture(name: string): void {
        const data = this.cacheData.get(name);

        if (data) {
            this.destroy(data);
        }
    }

    clear(): void {
        this.cacheData.forEach(val => this.destroy(val));
        this.cacheData.clear();
    }

    private generate(data: CacheData) {
        let index = 0;
        const renderer = coreStore.renderer.value!;
        const {provider, region, scaleMode, resolution} = data;
        const providerIsFunction = provider instanceof Function;
        const container = providerIsFunction ? provider() : provider;
        const {parent} = container;

        if (parent) {
            index = parent.children.indexOf(container);
            parent.removeChild(container);
        }

        data.texture = renderer.generateTexture(container, {
            scaleMode: scaleMode ?? SCALE_MODES.LINEAR,
            resolution: resolution ?? this.resolution,
            region
        });

        parent?.addChildAt(container, index);
    }

    private destroy(data: CacheData) {
        if (!data.texture) {
            return;
        }

        data.texture.destroy(true);
        data.texture = undefined;
    }
}

export const cacheAsSprite = new CacheAsSprite();